// InfoIcon — the shared info `(i)` affordance across the React islands (#272).
// Used by the input instruments and the results island (headline metrics,
// index table); lives at components/ rather than inputs/ because it is not
// input-specific.
//
// Replaces the per-island hand-rolled CSS `::after` tooltip, which was clipped
// by the instrument panel's `overflow: hidden` (rounded-card clip) whenever an
// icon sat near the panel's right edge (#269, reported on Car Tax). Radix
// Tooltip renders the bubble in a portal on `document.body` — so it escapes the
// panel clip — and is collision-aware, flipping/shifting to stay in frame.
//
// The glyph is the same Bootstrap `info-circle` the shell uses
// (parameter_info.R via bsicons), so the per-control icon matches the
// section-header icon — one visual language. The accessible name is the shell's
// "Parameter info"; the description is the tooltip content (Radix wires it as
// the trigger's aria-describedby when shown).

import * as Tooltip from "@radix-ui/react-tooltip";

// Bootstrap Icons "info-circle" (MIT) — inlined so it matches the shell glyph
// pixel-for-pixel without pulling an icon runtime into the bundle.
function InfoCircle(): JSX.Element {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
      <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
    </svg>
  );
}

export function InfoIcon({
  description,
  label = "Parameter info",
}: {
  description: string;
  /** Accessible name for the trigger. Defaults to the shell's "Parameter
   *  info"; pass a specific name (e.g. "Gini coefficient — what is it?") when
   *  several icons share a surface so their accessible names stay distinct. */
  label?: string;
}): JSX.Element {
  return (
    // Provider per-icon: each island mounts independently via its own
    // createRoot, so there's no single app-root to host a shared provider.
    <Tooltip.Provider delayDuration={200}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <button type="button" className="beamm-info-icon" aria-label={label}>
            <InfoCircle />
          </button>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            className="beamm-info-tooltip"
            side="top"
            align="center"
            sideOffset={6}
            collisionPadding={8}
          >
            {description}
            <Tooltip.Arrow className="beamm-info-tooltip__arrow" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}
